import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ProductServ from "../service/ProductServ";
import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import { message, Button, Typography, Row, Col, Spin, Tooltip, Skeleton } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { IconHeart, IconHeartFilled, IconPlus, IconMinus, IconUser, IconArrowRight, IconMoodEmpty, IconTruckDelivery } from "@tabler/icons-react";
import SafeImage, { getImageUrl } from "./SafeImage";
import CheckoutModal from "../component/CheckoutPage";
import ProductFeedbackServ from "../service/ProductFeedbackServ";
import ProductCard from "./ProductCard";
import { jwtDecode } from "jwt-decode";
import Navbar from "../pages/Navbar";
import Footer from "../component/Footer";
import AuthServ from "../service/AuthServ";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import ChatServ from "../service/ChatServ";
import { useProductSelection } from "../util/useProductSelection";
import { formatUnit, capitalizeFirst, renderSafeText } from "../util/unitFormatter";
import { LocationContext } from "../LocationContext";
import DealTimer from "./DealTimer";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import { Swiper, SwiperSlide } from 'swiper/react';
import GlobalTooltip from "./GlobalTooltip";
import { FreeMode, Navigation, Mousewheel } from 'swiper/modules';
import { calculateUnitPrice } from "../util/unitFormatter";
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import "../css/ProductSingle.css";
import Notify from "../util/Notify";

const { Title, Text, Paragraph } = Typography;

const formatOptionName = (name) => {
    if (!name) return "";
    let trimmed = name.trim();
    const upper = trimmed.toUpperCase();
    // New labels
    if (upper === 'KILOGRAMS (KG)') return 'Select Kilograms (kg)';
    if (upper === 'LITERS (L)') return 'Select Liters (L)';
    if (upper === 'PIECES (PCS)') return 'Select Pieces (pcs)';
    if (upper === 'PACK') return 'Select Pack';
    if (upper === 'SIZE') return 'Select Size';
    // Backward compat for legacy labels
    if (upper === 'WEIGHT') return 'Select Kilograms (kg)';
    if (upper === 'VOLUME') return 'Select Liters (L)';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const ProductSingle = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { showAlert } = useGlobalAuthModal();

    const queryParams = new URL(window.location.href).searchParams;
    const dealPrice = location.state?.dealPrice || queryParams.get("dealPrice");
    const dealEndDate = location.state?.dealEndDate || queryParams.get("dealEndDate");
    const dealTotalDiscount = location.state?.dealTotalDiscount || queryParams.get("dealTotalDiscount");

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState("");
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [localShopStatus, setLocalShopStatus] = useState({
        isOpen: true,
        reopenMessage: ""
    });

    const [selectedVariantId, setSelectedVariantId] = useProductSelection(id);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [isDescExpanded, setIsDescExpanded] = useState(false);

    const [selectedOptions, setSelectedOptions] = useState({});
    const [lastClickedOptionName, setLastClickedOptionName] = useState(null);
    const { cart, addToCart, updateQuantity, removeFromCart } = useContext(CartContext);
    const { toggleWishlist, isInWishlist } = useContext(WishlistContext);
    const { latitude, longitude } = useContext(LocationContext);
    const { formatETA } = useDeliveryEstimate(product?.shopId?._id || product?.shopId, latitude, longitude);

    const cartItem = cart?.items?.find(item => {
        const isSameProduct = (item.product?._id || item.product) === id;
        const areOptionsSame = (opts1 = {}, opts2 = {}) => {
            const keys1 = Object.keys(opts1);
            const keys2 = Object.keys(opts2);
            if (keys1.length !== keys2.length) return false;
            return keys1.every(key => opts1[key] === opts2[key]);
        };
        return isSameProduct && areOptionsSame(item.selectedOptions, selectedOptions);
    });
    const quantity = cartItem ? cartItem.quantity : 0;
    const isWishlisted = isInWishlist(id);

    // ⭐ Review STATES
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    // ⭐ Review List
    const [reviews, setReviews] = useState([]);
    const [showAllReviews, setShowAllReviews] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);

    const sortedReviews = React.useMemo(() => {
        return [...reviews].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }, [reviews]);

    const displayedReviews = showAllReviews ? sortedReviews : sortedReviews.slice(0, 3);

    // 💬 CHAT STATES
    const [chatOpen, setChatOpen] = useState(false);
    const [activeConversation, setActiveConversation] = useState(null);
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // 🔍 Magnifier States
    const [magnifierStyle, setMagnifierStyle] = useState({ display: 'none' });
    const [lensStyle, setLensStyle] = useState({ display: 'none' });
    const [isMagnifierVisible, setIsMagnifierVisible] = useState(false);
    const imgRef = React.useRef(null);
    const containerRef = React.useRef(null);

    const handleMouseMove = (e) => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const { top, left, width, height } = container.getBoundingClientRect();

        // Calculate relative position
        const x = e.pageX - left - window.scrollX;
        const y = e.pageY - top - window.scrollY;

        const lensWidth = 150;
        const lensHeight = 150;

        let posX = x - lensWidth / 2;
        let posY = y - lensHeight / 2;

        // Constraint within image box
        if (posX < 0) posX = 0;
        if (posY < 0) posY = 0;
        if (posX > width - lensWidth) posX = width - lensWidth;
        if (posY > height - lensHeight) posY = height - lensHeight;

        setLensStyle({
            display: 'block',
            left: `${posX}px`,
            top: `${posY}px`,
            width: `${lensWidth}px`,
            height: `${lensHeight}px`
        });

        // Zoom 2x
        const zoom = 2;
        const bgX = (posX / (width - lensWidth)) * 100;
        const bgY = (posY / (height - lensHeight)) * 100;

        setMagnifierStyle({
            display: 'block',
            backgroundImage: `url(${getImageUrl(activeImage)})`,
            backgroundSize: `${width * zoom}px ${height * zoom}px`,
            backgroundPosition: `${bgX}% ${bgY}%`
        });
    };

    const handleMouseEnter = () => setIsMagnifierVisible(true);
    const handleMouseLeave = () => {
        setIsMagnifierVisible(false);
        setMagnifierStyle({ display: 'none' });
        setLensStyle({ display: 'none' });
    };

    useEffect(() => {
        const handleShopStatusUpdate = (e) => {
            const data = e.detail;
            const currentShopId = product?.shopId?._id || product?.shopId;
            if (data?.shopId && String(data.shopId) === String(currentShopId)) {
                setLocalShopStatus({
                    isOpen: data.availability?.isOpen ?? true,
                    reopenMessage: data.availability?.reopenMessage || ""
                });
            }
        };

        window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
        return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
    }, [product]);

    useEffect(() => {
        const fetchProductAndReviews = async () => {
            if (!id || id === 'undefined') {
                setError("Invalid Product ID");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Load Product
                console.log("Fetching product details for ID:", id);
                const res = await ProductServ.single(id);
                const productData = res?.data !== undefined ? res.data : res;

                if (!productData || (typeof productData === 'object' && !productData.name && !productData._id)) {
                    throw new Error("Product not found");
                }

                setProduct(productData);

                // Set initial shop status
                if (productData.shopId && productData.shopId.business) {
                    const biz = productData.shopId.business;
                    const isClosed = biz.isOpen === false || biz.isTemporarilyClosed === true;

                    let reopenMsg = "Closed";
                    if (isClosed) {
                        if (biz.reopenTimeUnknown) {
                            reopenMsg = "Reopening time not announced yet.";
                        } else if (biz.closedUntil) {
                            const reopenDate = new Date(biz.closedUntil);
                            reopenMsg = `Reopens at ${reopenDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })} on ${reopenDate.toLocaleDateString()}`;
                        } else {
                            reopenMsg = "Closed until further notice.";
                        }
                    }

                    setLocalShopStatus({
                        isOpen: !isClosed,
                        reopenMessage: isClosed ? reopenMsg : ""
                    });
                }

                // Load Reviews
                try {
                    const reviewRes = await ProductFeedbackServ.index({ filter: JSON.stringify({ product: id }) });
                    setReviews(reviewRes?.data || reviewRes || []);
                } catch (revErr) {
                    console.warn("Could not load reviews:", revErr);
                }

                // Load Related Products
                try {
                    const relatedRes = await ProductServ.getRelatedProducts(id);
                    setRelatedProducts(relatedRes?.data || relatedRes || []);
                } catch (relErr) {
                    console.warn("Could not load related products:", relErr);
                }
            } catch (err) {
                console.error("CRITICAL: Error loading product details:", err);
                setError(err.message || "Could not load product");
            } finally {
                setLoading(false);
            }
        };

        fetchProductAndReviews();
    }, [id]);

    const prevProductId = React.useRef(null);

    // 1. Initialize selectedOptions when product or selectedVariantId loads
    useEffect(() => {
        if (!product) return;

        setSelectedOptions(prev => {
            const isNewProduct = prevProductId.current !== product._id;

            // If we've already initialized for this product, don't overwrite user selections
            if (!isNewProduct && Object.keys(prev).length > 0) {
                return prev;
            }

            prevProductId.current = product._id;

            let initialOptions = {};

            // Helper: Initialize all option groups to their first variant's value
            if (product.options && product.options.length > 0) {
                product.options.forEach(optGroup => {
                    if (optGroup.variants && optGroup.variants.length > 0) {
                        initialOptions[optGroup.name] = optGroup.variants[0].value || optGroup.variants[0].val;
                    }
                });
            } else if (product.variants && product.variants.length > 0) {
                const type = product.variantType || "Option";
                initialOptions[type] = product.variants[0].value || product.variants[0].val;
            }

            // Overlay with selectedVariantId if it exists
            if (selectedVariantId) {
                let found = false;

                // Try to match SKU combinations first
                if (product.skuList && product.skuList.length > 0) {
                    const skuItem = product.skuList.find(v => String(v._id || v.sku) === String(selectedVariantId));
                    if (skuItem && skuItem.options) {
                        initialOptions = { ...initialOptions, ...skuItem.options };
                        found = true;
                    }
                }

                // Try to match Nested option groups
                if (!found && product.options && product.options.length > 0) {
                    for (const optGroup of product.options) {
                        const variant = optGroup.variants?.find(v => String(v._id || v.value || v.val) === String(selectedVariantId));
                        if (variant) {
                            initialOptions[optGroup.name] = variant.value || variant.val;
                            found = true;
                            break;
                        }
                    }
                }

                // Try to match Legacy variants list
                if (!found && product.variants && product.variants.length > 0) {
                    const variant = product.variants.find(v => String(v._id || v.value || v.val) === String(selectedVariantId));
                    if (variant) {
                        initialOptions[product.variantType || "Option"] = variant.value || variant.val;
                        found = true;
                    }
                }
            }

            return initialOptions;
        });
    }, [product, selectedVariantId]);

    // 2. Derive selectedVariant based on selectedOptions
    useEffect(() => {
        if (!product) return;

        let matchedVariant = null;

        // 2a. Match SKU list first
        if (product.skuList && product.skuList.length > 0) {
            matchedVariant = product.skuList.find(skuItem => {
                if (!skuItem.options) return false;
                for (const key of Object.keys(selectedOptions)) {
                    if (selectedOptions[key] !== skuItem.options[key]) {
                        return false;
                    }
                }
                return true;
            });
        }

        // 2b. Fallback to Option Groups
        if (!matchedVariant && product.options && product.options.length > 0) {
            // Priority 1: Last clicked option
            if (lastClickedOptionName) {
                const optGroup = product.options.find(g => g.name === lastClickedOptionName);
                if (optGroup && optGroup.variants) {
                    const variant = optGroup.variants.find(v => (v.value || v.val) === selectedOptions[lastClickedOptionName]);
                    if (variant) {
                        matchedVariant = variant;
                    }
                }
            }

            // Priority 2: Iterate and find the first one that has a price different from base price
            if (!matchedVariant) {
                for (const optGroup of product.options) {
                    const selectedValue = selectedOptions[optGroup.name];
                    if (selectedValue && optGroup.variants) {
                        const variant = optGroup.variants.find(v => (v.value || v.val) === selectedValue);
                        if (variant && (variant.pri !== undefined ? variant.pri : variant.price) !== product.price) {
                            matchedVariant = variant;
                            break;
                        }
                    }
                }
            }

            // Priority 3: Default behavior (first group)
            if (!matchedVariant) {
                for (const optGroup of product.options) {
                    const selectedValue = selectedOptions[optGroup.name];
                    if (selectedValue && optGroup.variants) {
                        const variant = optGroup.variants.find(v => (v.value || v.val) === selectedValue);
                        if (variant) {
                            matchedVariant = variant;
                            break;
                        }
                    }
                }
            }
        }

        // 2c. Fallback to Legacy Variants list
        if (!matchedVariant && product.variants && product.variants.length > 0) {
            const type = product.variantType || "Option";
            const val = selectedOptions[type];
            if (val) {
                matchedVariant = product.variants.find(v => (v.value || v.val) === val);
            }
        }

        // 2d. Default fallback to first variant
        if (!matchedVariant) {
            if (product.skuList && product.skuList.length > 0) {
                matchedVariant = product.skuList[0];
            } else if (product.options && product.options.length > 0) {
                for (const optGroup of product.options) {
                    if (optGroup.variants && optGroup.variants.length > 0) {
                        matchedVariant = optGroup.variants[0];
                        break;
                    }
                }
            } else if (product.variants && product.variants.length > 0) {
                matchedVariant = product.variants[0];
            }
        }

        if (matchedVariant) {
            const newVariantId = matchedVariant._id || matchedVariant.value || matchedVariant.val;
            if (newVariantId && String(selectedVariantId) !== String(newVariantId)) {
                setSelectedVariantId(newVariantId);
            }
            setSelectedVariant(prev => {
                if (prev && (prev._id || prev.value || prev.val) === newVariantId) return prev;
                return matchedVariant;
            });

            const targetImg = matchedVariant.imageUrl || product.imageUrl || product.image || (product.images && product.images[0]) || "";
            if (targetImg) {
                setActiveImage(prev => (prev === targetImg ? prev : targetImg));
            }
        }
    }, [product, selectedOptions, lastClickedOptionName, selectedVariantId, setSelectedVariantId]);

    // 🔄 Responsive Update for Slider Direction (Must be before early returns)
    const [sliderDirection, setSliderDirection] = useState(() => window.innerWidth <= 992 ? "horizontal" : "vertical");

    useEffect(() => {
        const handleResize = () => {
            const newDir = window.innerWidth <= 992 ? "horizontal" : "vertical";
            setSliderDirection(prev => (prev === newDir ? prev : newDir));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="ps-container">
                    <div className="ps-main-layout">
                        {/* LEFT SIDE: SKELETON IMAGES */}
                        <div className="ps-left-section">
                            <div className="ps-thumbs-container">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="ps-thumb-slide">
                                        <Skeleton.Button active size="large" style={{ width: 80, height: 80, borderRadius: 8 }} />
                                    </div>
                                ))}
                            </div>
                            <div className="ps-image-box-wrapper">
                                <Skeleton.Button active style={{ width: 300, height: 300, borderRadius: 12 }} />
                                <div className="ps-image-actions">
                                    <Skeleton.Button active block size="large" style={{ height: 45, borderRadius: 8 }} />
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SIDE: SKELETON INFO */}
                        <div className="ps-right-section">
                            <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 1, width: '40%' }} />
                            <div style={{ marginTop: 20 }}>
                                <Skeleton active paragraph={{ rows: 3 }} />
                            </div>
                            <div style={{ marginTop: 20 }}>
                                <Skeleton.Button active style={{ width: 120, height: 40 }} />
                            </div>
                            <div style={{ marginTop: 30 }}>
                                <Skeleton active paragraph={{ rows: 2, width: ['60%', '50%'] }} />
                            </div>
                            <div style={{ marginTop: 30, display: 'flex', gap: 12 }}>
                                <Skeleton.Button active style={{ width: 150, height: 45, borderRadius: 8 }} />
                                <Skeleton.Button active style={{ width: 150, height: 45, borderRadius: 8 }} />
                            </div>
                        </div>
                    </div>

                    {/* Related Products Skeleton */}
                    <div className="ps-reviews-bottom">
                        <Skeleton active title={{ width: 200 }} paragraph={false} />
                        <div className="ps-reviews-grid" style={{ marginTop: 20 }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="ps-review-card">
                                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !product) {
        return (
            <>
                <Navbar />
                <div className="pcu-h-70vh pcu-flex-column pcu-flex-center pcu-color-bg-main pcu-padding-20">
                    <IconMoodEmpty size={64} className="pcu-color-muted" stroke={1.5} />
                    <h2 className="pcu-margin-top-20 pcu-color-title">{error || "Product not found"}</h2>
                    <p className="pcu-color-muted pcu-margin-bottom-30">We couldn't find the product you're looking for.</p>
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => navigate('/')}
                        className="pcu-bg-orange pcu-border-none pcu-radius-8 pcu-h-auto pcu-padding-y-12 pcu-padding-x-40"
                    >
                        Back to Shopping
                    </Button>
                </div>
                <Footer />
            </>
        );
    }

    const unitPrice = selectedVariant ? (selectedVariant.pri !== undefined ? selectedVariant.pri : selectedVariant.price) : (dealPrice || product.price);
    const unitMrp = selectedVariant ? (selectedVariant.mrp || product.mrp) : product.mrp;

    // Legacy handler
    const handleOptionSelect = (optionName, value) => {
        console.log(`🔵 Option selected: ${optionName} = ${value}`);
        setLastClickedOptionName(optionName);
        setSelectedOptions(prev => ({ ...prev, [optionName]: value }));
    };

    // ------------------- ADD/UPDATE CART -------------------
    const handleAddToCart = async () => {
        console.log('🛒 handleAddToCart called');
        console.log('🛒 Selected options:', selectedOptions);
        console.log('🛒 Product ID:', id);

        // Pass selectedOptions (which now contains { [Type]: Value })
        console.log('✅ Calling addToCart with:', { id, quantity: 1, selectedOptions });
        const formattedVariant = Object.keys(selectedOptions)
            .map(key => formatUnit(selectedOptions[key], key))
            .filter(Boolean)
            .join(" / ");

        await addToCart(id, 1, selectedOptions, {
            name: product.name,
            image: activeImage || product.imageUrl,
            variant: formattedVariant || null,
            price: unitPrice,
            mrp: unitMrp,
            shopId: product.shopId?._id || product.shopId
        });
    };

    const handleBuyNow = async () => {
        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to proceed to checkout.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }

        // If not in cart, add it first so we have a valid Cart Item ID for updates
        if (quantity === 0) {
            const formattedVariant = Object.keys(selectedOptions)
                .map(key => formatUnit(selectedOptions[key], key))
                .filter(Boolean)
                .join(" / ");
            await addToCart(id, 1, selectedOptions, {
                name: product.name,
                image: activeImage || product.imageUrl,
                variant: formattedVariant || null,
                price: unitPrice,
                mrp: unitMrp,
                shopId: product.shopId?._id || product.shopId
            });
        }

        setCheckoutOpen(true);
    };

    const handleIncrease = async () => {
        if (cartItem?._id) {
            await updateQuantity(cartItem._id, quantity + 1);
        }
    };

    const handleDecrease = async () => {
        if (cartItem?._id) {
            if (quantity > 1) {
                await updateQuantity(cartItem._id, quantity - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    const handleWishlistToggle = async () => {
        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to use the wishlist.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }
        await toggleWishlist(id);
    };

    const submitReview = async () => {
        const token =
            localStorage.getItem("customerToken") ||
            localStorage.getItem("token");

        if (!token || token === "null" || token === "undefined") {
            showAlert({
                title: "Continue Shopping",
                description: "Please login or register to submit a review.",
                buttons: [
                    {
                        text: "Cancel",
                        variant: "secondary"
                    },
                    {
                        text: "Login",
                        variant: "primary",
                        onClick: () => navigate("/customer-login")
                    }
                ]
            });
            return;
        }

        const decoded = jwtDecode(token);
        const userId = decoded?.id || decoded?.customerId || decoded?._id;

        const payload = {
            product: id,
            user: userId,
            rating,
            comment,
            createdBy: userId,
        };

        try {
            const res = await ProductFeedbackServ.create(payload);
            setRating(res);
            setComment("");
            Notify.success("Thank you for your feedback!");
        } catch (err) {
            Notify.error("Error", err.response?.data?.message || err.message || "Could not submit review");
        }
    };

    const categoryDescription =

        product.categoryId?.description;

    console.log('📦 Product data loaded:', categoryDescription);


    return (
        <>
            <Navbar />
            <div className="ps-container">
                {/* BLOCKED STATE OVERLAY IF SHOP IS CLOSED */}
                {!localShopStatus.isOpen && (
                    <div className="ps-blocked-overlay" style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.6)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9' }}>
                            <div style={{ background: '#fef2f2', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                <IconMoodEmpty size={40} color="#dc2626" />
                            </div>
                            <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginBottom: '12px' }}>🏪 Shop Currently Closed</h2>
                            <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
                                This shop is temporarily unavailable.<br />
                                <strong style={{ color: '#0f172a' }}>{localShopStatus.reopenMessage}</strong>
                            </p>
                            <Button type="primary" size="large" onClick={() => navigate('/')} style={{ borderRadius: '8px', fontWeight: 600, height: '44px', padding: '0 30px' }}>
                                Browse Other Shops
                            </Button>
                        </div>
                    </div>
                )}

                <div className="ps-main-layout" style={{ position: 'relative' }}>
                    {/* LEFT SIDE: IMAGES (Swiper Slider) */}
                    <div className="ps-left-section">
                        {/* Thumbnails Swiper */}
                        <div className="ps-thumbs-container">
                            <Swiper
                                key={sliderDirection}
                                onSwiper={setThumbsSwiper}
                                direction={sliderDirection}
                                spaceBetween={10}
                                slidesPerView={3}
                                watchSlidesProgress={true}
                                observer={true}
                                observeParents={true}
                                breakpoints={{
                                    320: { slidesPerView: 4 },
                                    768: { slidesPerView: 5 },
                                    993: { slidesPerView: 3 }
                                }}
                                freeMode={true}
                                mousewheel={true}
                                modules={[FreeMode, Navigation, Mousewheel]}
                                className="ps-thumbs-swiper"
                            >
                                {([product.imageUrl, product.image, ...(product.images || [])].filter((url, index, self) => url && self.indexOf(url) === index)).map((img, i) => (
                                    <SwiperSlide key={i} className="ps-thumb-slide">
                                        <div
                                            className={`ps-thumb-wrapper ${activeImage === img ? "active" : ""}`}
                                            onClick={() => setActiveImage(img)}
                                        >
                                            <SafeImage
                                                src={img}
                                                alt={`Thumb ${i}`}
                                                placeholderText="Img"
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>

                        {/* Main Image (Static, controlled by thumbs) */}
                        <div className="ps-image-box-wrapper">
                            <div className="ps-image-box">
                                <div
                                    className="ps-main-img-container"
                                    ref={containerRef}
                                    onMouseMove={handleMouseMove}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <SafeImage
                                        src={activeImage || product.imageUrl || product.image || (product.images && product.images[0])}
                                        className="ps-main-img"
                                        alt={product.name}
                                        placeholderText="Image Unavailable"
                                    />
                                    {isMagnifierVisible && <div className="ps-magnifier-lens" style={lensStyle} />}

                                    {/* ❤️ Wishlist Floating Button */}
                                    <button
                                        className={`ps-wishlist-floating ${isWishlisted ? "active" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleWishlistToggle();
                                        }}
                                    >
                                        {isWishlisted ? <IconHeartFilled size={22} /> : <IconHeart size={22} />}
                                    </button>
                                </div>

                                <div
                                    className={`ps-magnifier-result ${isMagnifierVisible ? 'visible' : ''}`}
                                    style={magnifierStyle}
                                />
                            </div>

                            {/* 🛒 Add to Cart / Quantity - Moved below image */}
                            <div className="ps-image-actions">
                                {!localShopStatus.isOpen ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '8px' }}>
                                        <button
                                            className="ps-btn ps-full-width pcu-border-none pcu-font-16 pcu-font-bold pcu-padding-y-12 pcu-radius-8 pcu-opacity-80 pcu-cursor-not-allowed"
                                            style={{ color: "#999", backgroundColor: "#f5f5f5" }}
                                            disabled
                                        >
                                            Unavailable
                                        </button>
                                        <Text type="danger" style={{ fontWeight: 'bold' }}>
                                            {localShopStatus.reopenMessage || "Closed"}
                                        </Text>
                                    </div>
                                ) : quantity > 0 ? (
                                    <div className="ps-quantity-panel ps-full-width">
                                        <Button icon={<IconMinus size={18} />} onClick={handleDecrease} />
                                        <span className="ps-quantity-val">{quantity}</span>
                                        <Button icon={<IconPlus size={18} />} onClick={handleIncrease} />
                                    </div>
                                ) : (() => {
                                    const vStock = selectedVariant ? (selectedVariant.stc !== undefined ? selectedVariant.stc : selectedVariant.stock) : undefined;
                                    const currentStock = vStock !== undefined ? vStock : (product.stock || 0);
                                    return (
                                        <button
                                            className={`ps-btn  ps-full-width pcu-border-none pcu-font-16 pcu-font-bold pcu-padding-y-12 pcu-radius-8 ${currentStock <= 0 ? 'pcu-opacity-50 pcu-cursor-not-allowed' : 'pcu-cursor-pointer'}`}
                                            style={{ color: "white", backgroundColor: "green" }}
                                            onClick={() => {
                                                if (currentStock > 0) {
                                                    handleAddToCart();
                                                }
                                            }}
                                            disabled={currentStock <= 0}
                                        >
                                            {currentStock <= 0 ? "Out of Stock" : "Add to Cart"}
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: PRODUCT INFO */}
                    <div className="ps-right-section">
                        <h1 className="ps-title">{renderSafeText(product.name)}</h1>

                        {product.rating > 0 && (
                            <div className="ps-rating-row">
                                <div className="ps-stars">
                                    {"★".repeat(Math.round(product.rating || 0))}
                                    {"☆".repeat(5 - Math.round(product.rating || 0))}
                                </div>
                                <span className="ps-rating-count">({product.rating})</span>
                                <span
                                    className="ps-review-link pcu-cursor-pointer"
                                    onClick={() =>
                                        document
                                            .getElementById("customer-reviews")
                                            ?.scrollIntoView({ behavior: "smooth" })
                                    }
                                >
                                    | {reviews.length} Reviews
                                </span>
                            </div>
                        )}

                        <p className="ps-short-desc">{product.description || "High quality product from Nearzon."}</p>

                        <div className="ps-price-row">
                            {(() => {
                                const qty = quantity > 0 ? quantity : 1;
                                const totalPrice = unitPrice * qty;
                                const totalMrp = unitMrp * qty;

                                return (
                                    <>
                                        <span className="ps-curr-price">₹{Math.round(totalPrice)}</span>

                                        {/* Logic for MRP Display */}
                                        {unitMrp && unitMrp !== unitPrice && (
                                            <>
                                                <span className="ps-old-price">
                                                    MRP ₹{Math.round(totalMrp)}
                                                </span>

                                                {/* Dynamic Discount Calculation */}
                                                <span className="ps-discount-badge">
                                                    {(() => {
                                                        const p = unitPrice;
                                                        const m = unitMrp;
                                                        if (!m || !p) return 0;
                                                        const percent = Math.round(((m - p) / m) * 100);
                                                        return percent > 0 ? `${percent}% OFF` : `${percent}%`;
                                                    })()}
                                                </span>
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>


                        {/* ✅ Delivery Badge moved closer to price for better alignment */}
                        {formatETA(product.shopId?._id || product.shopId, product.shopId?.business?.deliveryTime || product.shopId?.deliveryTime || product.shop?.deliveryTime || "30-45 mins") && (
                            <div className="pcu-delivery-badge-mini" style={{ marginBottom: '4px' }}>
                                <IconTruckDelivery
                                    size={13}
                                    stroke={3}
                                    color="#16a34a"
                                    style={{ marginRight: "4px" }}
                                />
                                <span>
                                    Delivery By{" "}
                                    {formatETA(
                                        product.shopId?._id || product.shopId,
                                        product.shopId?.business?.deliveryTime ||
                                        product.shopId?.deliveryTime ||
                                        product.shop?.deliveryTime ||
                                        "30-45 mins"
                                    )}
                                </span>
                            </div>
                        )}

                        {/* ✅ Show dynamic live countdown deal timer */}
                        {
                            (product.dealEligible || product.hasActiveDeal) && (
                                <div className="offer-end">
                                    <DealTimer
                                        endDate={product.dealEndDate || dealEndDate}
                                        variant="small"
                                        label={product.dealLabel}
                                    />
                                </div>
                            )
                        }

                        <div style={{ display: "flex", gap: "10px", marginBottom: '8px' }}>
                        </div>



                        {/* Product Options */}
                        {/* ✅ NEW: Nested Variant Options */}
                        {
                            product.options && product.options.length > 0 && product.options.map((optGroup, grpIdx) => {
                                if (!optGroup.variants || optGroup.variants.length === 0) return null;
                                const selectedVal = selectedOptions[optGroup.name];
                                return (
                                    <div className="ps-options-section" key={grpIdx}>
                                        <div className="ps-option-group">
                                            <h4 className="ps-option-name">{formatOptionName(optGroup.name)}</h4>
                                            <div className="ps-option-values">
                                                {optGroup.variants && [...optGroup.variants].sort((a, b) => ((a.pri || a.price || 0) - (b.pri || b.price || 0))).map((variant) => {
                                                    const isSelected = selectedVal === (variant.value || variant.val);
                                                    return (
                                                        <button
                                                            key={variant._id || variant.value}
                                                            className={`ps-option-btn ${isSelected ? "active" : ""} ${(variant.isActive === false || (variant.stc !== undefined ? variant.stc : variant.stock) <= 0) ? "pcu-opacity-50 pcu-cursor-not-allowed" : "pcu-cursor-pointer"}`}
                                                            onClick={() => {
                                                                handleOptionSelect(optGroup.name, variant.value || variant.val);
                                                            }}
                                                            disabled={variant.isActive === false || (variant.stc !== undefined ? variant.stc : variant.stock) <= 0}
                                                        >
                                                            {formatUnit(variant.value, optGroup.name)}
                                                            {isSelected && selectedVariant?._id === variant._id && quantity > 1 ? ` (${quantity} qty)` : ''}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        }

                        {/* Fallback with SMART GROUPING for mixed legacy variants */}
                        {
                            (!product.options || product.options.length === 0) && product.hasVariants && product.variants?.length > 0 && (
                                (() => {
                                    // Smartly group variants if they seem mixed (e.g. Kilograms/Liters)
                                    const variants = product.variants;
                                    const groups = {}; // { 'Kilograms (kg)': [vars], 'Liters (L)': [vars], 'Other': [vars] }

                                    variants.forEach(v => {
                                        let val = v.value.toString();
                                        let type = 'Other';

                                        // Infer type from variant value
                                        if (/[0-9]+.*(g|kg|gram|kilo)/i.test(val)) type = 'Kilograms (kg)';
                                        else if (/[0-9]+.*(ml|l|liter|litre)/i.test(val)) type = 'Liters (L)';
                                        else if (/[0-9]+.*(pcs|piece|pieces|unit)/i.test(val)) type = 'Pieces (pcs)';
                                        else {
                                            type = (product.variantType && product.variantType.includes('/')) ? 'Mixed' : (product.variantType || 'Option');
                                        }

                                        if (!groups[type]) groups[type] = [];
                                        groups[type].push(v);
                                    });

                                    const sortedKeys = Object.keys(groups).sort((a, b) => {
                                        if (a === 'Kilograms (kg)') return -1;
                                        if (b === 'Kilograms (kg)') return 1;
                                        if (a === 'Liters (L)') return -1;
                                        if (b === 'Liters (L)') return 1;
                                        return 0;
                                    });

                                    return sortedKeys.map((grpName, idx) => {
                                        const optGroupName = grpName === 'Mixed' || grpName === 'Other' ? (product.variantType || "Option") : grpName;
                                        const selectedVal = selectedOptions[optGroupName];
                                        return (
                                            <div className="ps-options-section" key={idx}>
                                                <div className="ps-option-group">
                                                    <h4 className="ps-option-name">{formatOptionName(optGroupName)}</h4>
                                                    <div className="ps-option-values">
                                                        {groups[grpName].map((variant) => {
                                                            const isSelected = selectedVal === (variant.value || variant.val);
                                                            return (
                                                                <button
                                                                    key={variant._id || variant.value}
                                                                    className={`ps-option-btn ${isSelected ? "active" : ""} ${(variant.isActive === false || (variant.stc !== undefined ? variant.stc : variant.stock) <= 0) ? "pcu-opacity-50 pcu-cursor-not-allowed" : "pcu-cursor-pointer"}`}
                                                                    onClick={() => {
                                                                        handleOptionSelect(optGroupName, variant.value || variant.val);
                                                                    }}
                                                                    disabled={variant.isActive === false || (variant.stc !== undefined ? variant.stc : variant.stock) <= 0}
                                                                >
                                                                    {formatUnit(variant.val || variant.value, grpName)}
                                                                    {isSelected && selectedVariant?._id === variant._id && quantity > 1 ? ` (${quantity} qty)` : ''}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    });
                                })()
                            )
                        }

                        {/* Legacy Options */}
                        {
                            !product.hasVariants && product.options && product.options.length > 0 && (
                                <div className="ps-options-section">
                                    {product.options.map((opt) => {
                                        if (!opt.option || opt.option.length === 0) return null;
                                        return (
                                            <div key={opt._id} className="ps-option-group">
                                                <h4 className="ps-option-name">{formatOptionName(opt.name)}</h4>
                                                <div className="ps-option-values">
                                                    {opt.option && opt.option.map((val) => (
                                                        <button
                                                            key={val}
                                                            className={`ps-option-btn ${selectedOptions[opt.name] === val ? "active" : ""}`}
                                                            onClick={() => handleOptionSelect(opt.name, val)}
                                                        >
                                                            {val}
                                                            {selectedOptions[opt.name] === val && quantity > 1 ? ` (${quantity} qty)` : ''}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        }

                        <CheckoutModal
                            open={checkoutOpen}
                            onClose={() => setCheckoutOpen(false)}
                            items={[{
                                _id: cartItem?._id || product._id, // Pass Cart Item ID if available for updateQuantity to work
                                product: {
                                    ...product,
                                    price: Math.round(unitPrice), // Wrap price in product object (or use p.price logic)
                                    mrp: Math.round(unitMrp)
                                },
                                quantity: quantity > 0 ? quantity : 1, // Logic handled by auto-addToCart above mostly
                                selectedOptions
                            }]}
                        />

                        {/* 🏪 Shop Info - Moved back here */}
                        {
                            product.shopId && (
                                <div
                                    className="ps-shop-card"
                                    onClick={() => navigate(`/products/${product.shopId?._id || product.shopId}`)}
                                >
                                    <div className="ps-shop-card-left">
                                        <div className="ps-shop-logo-wrapper">
                                            <SafeImage
                                                src={product.shopId?.imageUrl || product.shopId?.shopImage}
                                                alt={product.shopId?.name || product.shopId?.shopName || product.shopName || "Seller"}
                                                className="ps-shop-logo"
                                                placeholderText="Shop"
                                            />
                                        </div>
                                    </div>
                                    <div className="ps-shop-card-right">
                                        {product.shopId?.isSubscribed && (
                                            <div className="ps-shop-badge">Official Store</div>
                                        )}
                                        <GlobalTooltip title={product.shopId?.name || product.shopId?.shopName || product.shopName || "Our Shop"}>
                                            <h3 className="ps-shop-name">{product.shopId?.name || product.shopId?.shopName || product.shopName || "Our Shop"}</h3>
                                        </GlobalTooltip>
                                        {(product.shopId?.description || product.shopId?.shopDescription) && (
                                            <GlobalTooltip title={product.shopId?.description || product.shopId?.shopDescription} placement="topLeft">
                                                <p className="ps-shop-tagline">
                                                    {product.shopId?.description || product.shopId?.shopDescription}
                                                </p>
                                            </GlobalTooltip>
                                        )}

                                        {product.shopId?.isSubscribed && (
                                            <div className="ps-view-store-btn">
                                                <span>Browse Store</span>
                                                <IconArrowRight size={14} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }

                        {/* 📋 UNIFIED PRODUCT SPECIFICATIONS & DETAILS */}
                        <div className="ps-details-container">
                            <div className="ps-specs-card">
                                <h4 className="ps-section-title">Information</h4>
                                <div className="ps-specs-grid">
                                    {/* Core Specs */}
                                    {product.category || product.categoryId?.name ? (
                                        <>
                                            <div className="ps-spec-label">Product Type</div>
                                            <div className="ps-spec-value">{renderSafeText(product.category || product.categoryId?.name)}</div>
                                        </>
                                    ) : null}

                                    {/* Brand (Deep Analyzed & Displayed Below Product Type) */}
                                    {(() => {
                                        const brandVal = product.brandName ||
                                            product.brandId?.name ||
                                            (typeof product.brand === 'string' ? product.brand : product.brand?.name) ||
                                            (product.brandId && typeof product.brandId === 'object' ? product.brandId.name : null);
                                        if (!brandVal) return null;
                                        return (
                                            <>
                                                <div className="ps-spec-label">Brand</div>
                                                <div className="ps-spec-value">{renderSafeText(brandVal)}</div>
                                            </>
                                        );
                                    })()}

                                    {/* {product.shopId?.business?.gstNumber && (
                                        <>
                                            <div className="ps-spec-label">GST Number</div>
                                            <div className="ps-spec-value">{renderSafeText(product.shopId.business.gstNumber)}</div>
                                        </>
                                    )} */}

                                    {categoryDescription && (
                                        <>
                                            <div className="ps-spec-label">Description</div>

                                            <div className="ps-spec-value">
                                                <div
                                                    className={`ps-description-text ${isDescExpanded ? "expanded" : "collapsed"
                                                        }`}
                                                >
                                                    {renderSafeText(categoryDescription)}
                                                </div>

                                                {categoryDescription.length > 250 && (
                                                    <button
                                                        className="ps-show-more-toggle"
                                                        onClick={() => setIsDescExpanded(!isDescExpanded)}
                                                    >
                                                        {isDescExpanded ? "Show Less" : "Show More"}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}


                                    {product.shopId?.business?.shopLicenseNumber && (
                                        <>
                                            <div className="ps-spec-label">Shop License</div>
                                            <div className="ps-spec-value">{renderSafeText(product.shopId.business.shopLicenseNumber)}</div>
                                        </>
                                    )}



                                    {/* Additional Info items merged into the same grid */}
                                    {product.additionalInfo && product.additionalInfo.length > 0 &&
                                        product.additionalInfo.map((item, idx) => (
                                            <React.Fragment key={idx}>
                                                <div className="ps-spec-label">{item.question}</div>
                                                <div className="ps-spec-value">{item.answer}</div>
                                            </React.Fragment>
                                        ))
                                    }
                                </div>
                            </div>

                            {/* Main Description */}
                            {/* {product.description && (
                                <div className="ps-desc-section">
                                    <h4 className="ps-section-title">Description</h4>
                                    <div className="ps-desc-content">
                                        <p className="ps-pre-wrap">{renderSafeText(product.description)}</p>
                                    </div>
                                </div>
                            )} */}
                        </div>


                        {/* ⭐ REVIEW INPUT SECTION */}
                        {/* <div className="ps-review-write-section">
                            <h3>Write a Review</h3>
                            <div className="ps-star-input">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        className={`star ${star <= rating ? "active" : ""}`}
                                        onClick={() => setRating(star)}
                                    >
                                        ★
                                    </span>
                                ))}
                            </div>

                            <textarea
                                className="ps-review-textarea"
                                placeholder="Write your review..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />

                            <Button className="ps-review-submit-btn" onClick={submitReview}>
                                Submit Review
                            </Button>
                        </div> */}
                    </div >
                </div>

                {relatedProducts.length > 0 && (
                    <div className="ps-related-section pcu-margin-top-50">
                        <Title level={4} className="pcu-margin-bottom-25">Related Products</Title>
                        <Row gutter={[16, 24]}>
                            {relatedProducts.slice(0, 6).map((p) => (
                                <Col xs={12} sm={12} md={8} lg={6} xl={4} key={p._id}>
                                    <ProductCard product={p} />
                                </Col>
                            ))}
                        </Row>
                        {relatedProducts.length > 6 && (
                            <div className="pcu-text-center pcu-margin-top-30">
                                <Button
                                    type="primary"
                                    shape="round"
                                    size="large"
                                    className="pcu-bg-purple pcu-border-none pcu-padding-x-40"
                                    onClick={() => navigate(`/all-products?categoryName=${product.category}`)}
                                >
                                    Show More
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* CUSTOMER REVIEWS BOTTOM SECTION */}
                <div className="ps-reviews-bottom" id="customer-reviews">
                    <h3>Customer Reviews</h3>

                    <div className="ps-reviews-list">
                        {displayedReviews.length === 0 ? (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "30px",
                                    background: "var(--color-bg-muted)",
                                    borderRadius: "12px",
                                }}
                            >
                                <Text strong style={{ color: "var(--color-text-muted)" }}>
                                    No feedback available yet
                                </Text>
                                <br />
                                <Text type="secondary">
                                    Be the first to leave a review for this product!
                                </Text>
                            </div>
                        ) : (
                            <>
                                <div className="ps-reviews-grid">
                                    {displayedReviews.map((r) => (
                                        <div key={r._id} className="ps-review-card">
                                            <div className="ps-review-header">
                                                <span className="ps-reviewer-icon">
                                                    <IconUser size={18} />
                                                </span>
                                                <div className="ps-reviewer-info">
                                                    <span className="ps-reviewer-name">
                                                        {renderSafeText(r.user?.firstName || "Anonymous")}{" "}
                                                        {renderSafeText(r.user?.lastName || "")}
                                                    </span>
                                                    <div className="ps-review-stars">
                                                        {"★".repeat(r.rating)}
                                                        {"☆".repeat(5 - r.rating)}
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="ps-review-comment">{r.comment}</p>
                                        </div>
                                    ))}
                                </div>
                                {sortedReviews.length > 3 && (
                                    <div className="ps-show-more-container">
                                        <button
                                            className="ps-show-more-btn"
                                            onClick={() => setShowAllReviews(!showAllReviews)}
                                        >
                                            {showAllReviews ? "Show Less" : `View All Reviews (${reviews.length})`}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* {(() => {
                    const price = selectedVariant ? (selectedVariant.pri !== undefined ? selectedVariant.pri : selectedVariant.price) : product.price;
                    const value = selectedVariant ? (selectedVariant.val || selectedVariant.value) : null;
                    const unitPrice = calculateUnitPrice(price, value);
                    if (!unitPrice) return null;
                    return (
                        <div className="ps-unit-price" style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', marginTop: '-4px' }}>
                            {unitPrice}
                        </div>
                    );
                })()} */}


                {/* <ChatWindow
                    open={chatOpen}
                    onClose={() => setChatOpen(false)}
                    conversation={activeConversation}
                    currentUser={currentUser}
                /> */}


            </div>
            <Footer />
        </>
    );
};

export default ProductSingle;
